import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { getLabels, formatTimestampFromDB } from './DashboardComponent';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const color = '#9b59b6';

// Funcție pentru calcularea direcției din mx și my
function calculateDirection(mx, my) {
  if (mx === 0 && my === 0) return 'N/A';
  
  // Calculează unghiul în radiani
  let angle = Math.atan2(my, mx);
  
  // Convertește la grade
  let degrees = angle * (180 / Math.PI);
  
  // Normalizează la 0-360 grade
  degrees = (degrees + 360) % 360;
  
  // Convertește la direcții cardinale
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(degrees / 45) % 8;
  
  return `${directions[index]} (${degrees.toFixed(1)}°)`;
}

export default function MagnetometerChart({ data, last, interval: propInterval, onIntervalChange }) {
  const [intervalValue, setInterval] = useState(propInterval || '1s');
  const chartRef = useRef();
  const interval = intervalValue;
  const submenuOptions = ['1s', '5s', '1m', '5m'];

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [data]);

  const chartData = useMemo(() => ({
    labels: getLabels(sortedData, interval),
    datasets: [
      {
        label: 'Mag X',
        data: sortedData.map(d => {
          const magnetometer = typeof d.magnetometer_sensors === 'string' ? JSON.parse(d.magnetometer_sensors) : d.magnetometer_sensors;
          return magnetometer?.xm || 0;
        }),
        borderColor: '#9b59b6',
        backgroundColor: 'rgba(155, 89, 182, 0.05)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#9b59b6' : 'rgba(155,89,182,0.3)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 6 : 2),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 2 : 1),
        borderWidth: 2,
        fill: false,
      },
      {
        label: 'Mag Y',
        data: sortedData.map(d => {
          const magnetometer = typeof d.magnetometer_sensors === 'string' ? JSON.parse(d.magnetometer_sensors) : d.magnetometer_sensors;
          return magnetometer?.ym || 0;
        }),
        borderColor: '#1abc9c',
        backgroundColor: 'rgba(26, 188, 156, 0.05)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#1abc9c' : 'rgba(26,188,156,0.3)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 6 : 2),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 2 : 1),
        borderWidth: 2,
        fill: false,
      },
      {
        label: 'Mag Z',
        data: sortedData.map(d => {
          const magnetometer = typeof d.magnetometer_sensors === 'string' ? JSON.parse(d.magnetometer_sensors) : d.magnetometer_sensors;
          return magnetometer?.zm || 0;
        }),
        borderColor: '#34495e',
        backgroundColor: 'rgba(52, 73, 94, 0.05)',
        pointBackgroundColor: sortedData.map((d, i) => i === sortedData.length - 1 ? '#34495e' : 'rgba(52,73,94,0.3)'),
        pointRadius: sortedData.map((d, i) => i === sortedData.length - 1 ? 6 : 2),
        pointBorderWidth: sortedData.map((d, i) => i === sortedData.length - 1 ? 2 : 1),
        borderWidth: 2,
        fill: false,
      }
    ],
  }), [sortedData, interval]);

  useEffect(() => {
    if (chartRef.current && chartRef.current.chartInstance) {
      const chart = chartRef.current.chartInstance;
      chart.data.labels = chartData.labels;
      chart.data.datasets = chartData.datasets;
      chart.update('none');
    }
  }, [chartData]);

  // Sincronizează intervalul cu prop-ul
  useEffect(() => {
    if (propInterval && propInterval !== interval) {
      setInterval(propInterval);
    }
  }, [propInterval, interval]);

  // LIVE dacă ultima valoare e în ultimele 10 minute
  const isLive = last && (Date.now() - new Date(last.created_at).getTime() < 10 * 60 * 1000);
  
  // Calculează valorile curente pentru magnetometru
  const currentValues = last ? (() => {
    const magnetometer = typeof last.magnetometer_sensors === 'string' ? JSON.parse(last.magnetometer_sensors) : last.magnetometer_sensors;
    const mx = magnetometer?.xm || 0;
    const my = magnetometer?.ym || 0;
    const mz = magnetometer?.zm || 0;
    return {
      magX: mx,
      magY: my,
      magZ: mz,
      direction: calculateDirection(mx, my)
    };
  })() : { magX: 0, magY: 0, magZ: 0, direction: 'N/A' };

  const lastTime = last ? formatTimestampFromDB(last.created_at) : '-';

  // Calculează axa Y dinamică pentru toate valorile
  const allValues = sortedData.flatMap(d => {
    const magnetometer = typeof d.magnetometer_sensors === 'string' ? JSON.parse(d.magnetometer_sensors) : d.magnetometer_sensors;
    return [
      magnetometer?.xm || 0,
      magnetometer?.ym || 0,
      magnetometer?.zm || 0
    ];
  }).filter(v => typeof v === 'number');

  let minY = 0, maxY = 100;
  if (allValues.length) {
    minY = Math.floor(Math.min(...allValues) - 0.1 * Math.abs(Math.min(...allValues)));
    maxY = Math.ceil(Math.max(...allValues) + 0.1 * Math.abs(Math.max(...allValues)));
    if (minY === maxY) { minY -= 5; maxY += 5; }
  }

  const handleIntervalChange = async (opt) => {
    setInterval(opt);
    if (onIntervalChange) {
      onIntervalChange('magnetometer', opt);
    }
  };

  // Adaug funcția de export CSV pentru magnetometru
  function exportCsv(data, imei) {
    const now = Date.now();
    const fifteenMinutesAgo = now - 15 * 60 * 1000;
    const filtered = data.filter(d =>
      d.imei === imei && new Date(d.created_at).getTime() >= fifteenMinutesAgo
    );
    const header = 'timestamp,imei,magnetometer_x,magnetometer_y,magnetometer_z,direction\n';
    const rows = filtered.map(d => {
      const magnetometer = typeof d.magnetometer_sensors === 'string' ? JSON.parse(d.magnetometer_sensors) : d.magnetometer_sensors;
      const mx = magnetometer?.xm || 0;
      const my = magnetometer?.ym || 0;
      const direction = calculateDirection(mx, my);
      return [
        d.created_at,
        d.imei,
        mx,
        my,
        magnetometer?.zm ?? '',
        direction
      ].join(',');
    });
    const csv = header + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `magnetometer_${imei}_${new Date().toISOString().slice(0,16).replace(/[:T]/g,'-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ marginBottom: 0, height: 160, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
      <div style={{ display: 'flex', alignItems: 'center', fontWeight: 500, fontSize: 12, marginBottom: 2, color, textTransform: 'uppercase', letterSpacing: 1, gap: 7, flexWrap: 'wrap' }}>
        <span style={{fontSize: 12}}>MAGNETOMETER</span>
        {isLive && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, marginLeft: 6 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#27ae60', animation: 'pulse 1s infinite' }} />
            <span style={{ fontWeight: 600, color: '#27ae60', fontSize: 11 }}>LIVE</span>
          </span>
        )}
        {submenuOptions.map(opt => (
          <button
            key={opt}
            onClick={() => handleIntervalChange(opt)}
            style={{
              padding: '1px 7px',
              borderRadius: 14,
              border: interval === opt ? '2px solid #9b59b6' : '1px solid #bbb',
              background: interval === opt ? '#f8f4ff' : '#fff',
              color: interval === opt ? '#9b59b6' : '#222',
              fontWeight: interval === opt ? 'bold' : 'normal',
              fontSize: 10,
              cursor: 'pointer',
              boxShadow: interval === opt ? '0 0 0 2px #9b59b655' : '0 1px 2px #eee',
              transition: 'all 0.2s',
              outline: interval === opt ? 'none' : undefined,
              marginLeft: 3
            }}
            tabIndex={-1}
            title="Perioada de colectare"
          >
            {opt}
          </button>
        ))}
        {/* Buton export CSV */}
        <button
          style={{
            marginLeft: 8,
            padding: '2px 8px',
            fontSize: 11,
            borderRadius: 6,
            border: '1px solid #9b59b6',
            background: '#f8f4ff',
            color: '#9b59b6',
            cursor: 'pointer',
            fontWeight: 500
          }}
          title="Exportă CSV magnetometru"
          onClick={() => exportCsv(data, last?.imei)}
        >
          Export CSV
        </button>
        <span style={{ fontSize: 10, color: '#888', marginLeft: 6, fontWeight: 400 }}>{lastTime}</span>
      </div>
      
      {/* Current values for magnetometer */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 6, 
        marginBottom: 12, 
        padding: '10px 14px', 
        background: 'rgba(255,255,255,0.9)', 
        borderRadius: 10, 
        border: '1px solid #ddd',
        fontSize: 12,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: color, minWidth: 70 }}>Magnetometer:</span>
          <span style={{ color: '#9b59b6', fontWeight: 500 }}>X: {currentValues.magX.toFixed(3)} μT</span>
          <span style={{ color: '#1abc9c', fontWeight: 500 }}>Y: {currentValues.magY.toFixed(3)} μT</span>
          <span style={{ color: '#34495e', fontWeight: 500 }}>Z: {currentValues.magZ.toFixed(3)} μT</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: '#e67e22', minWidth: 70 }}>Direction:</span>
          <span style={{ color: '#e67e22', fontWeight: 500, fontSize: 12 }}>🧭 {currentValues.direction}</span>
        </div>
      </div>
      
      <div style={{ height: 280, marginTop: 8 }}>
        <Line ref={chartRef} data={chartData} options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                usePointStyle: true,
                padding: 15,
                font: {
                  size: 13
                },
                boxWidth: 20,
                boxHeight: 8
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label + ': ' + context.parsed.y.toFixed(2);
                  if (context.dataset.label.includes('Mag')) {
                    label += ' μT';
                  }
                  return label;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              min: minY,
              max: maxY,
              grid: {
                color: 'rgba(0,0,0,0.05)',
              },
              ticks: {
                font: {
                  size: 12
                },
                padding: 10
              }
            },
            x: {
              grid: {
                color: 'rgba(0,0,0,0.05)',
              },
              ticks: {
                font: {
                  size: 12
                },
                padding: 8,
                maxTicksLimit: 8
              }
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          }
        }} />
      </div>
    </div>
  );
} 